'use client';

import {
    EnergyUsageInput,
    EnergyUsageOutput,
} from '@/lib/actions/usage/constants';

import { getOnInitTrue } from '@/ui/atoms/form-atoms';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCustomerInputs } from './useCustomerInputs';

const loadingAtom = atom(false);

const energyUsageLatestInputAtom = atomWithStorage<
    EnergyUsageInput | undefined
>('energyUsageLatestInput', undefined, undefined, getOnInitTrue);

const energyUsageResultAtom = atomWithStorage<EnergyUsageOutput | undefined>(
    'energyUsageResult',
    undefined,
    undefined,
    getOnInitTrue
);

const energyUsageResultInputAtom = atomWithStorage<
    EnergyUsageInput | undefined
>('energyUsageResultInput', undefined, undefined, getOnInitTrue);

const errorMessageAtom = atom<string>('');

const DEBOUNCE_DELAY_MS = 1000;

// API route for the usage calculator
const USAGE_CALCULATOR_API_ROUTE = '/api/usage-calculator';

// API client function to call our internal API route
async function callUsageCalculator(inputDict: EnergyUsageInput) {
    console.log('Calling internal usage calculator API route');

    try {
        const response = await fetch(USAGE_CALCULATOR_API_ROUTE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(inputDict),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText);
        }

        const data = await response.json();
        console.log('API response:', data);

        if (data.error) {
            throw new Error(data.error);
        }

        return data;
    } catch (error) {
        console.error('Error calling usage calculator:', error);
        throw error;
    }
}

function useIsInputComplete() {
    const { inputDict, inputError } = useCustomerInputs();

    return useMemo(() => {
        // At minimum, we need zip code and household size for any inference type
        const hasMinimumRequirements = Boolean(
            inputDict.zip && inputDict.hh_size !== undefined
        );

        // Check if we have enough for HVAC (most comprehensive)
        const hasHvacRequirements =
            hasMinimumRequirements &&
            Boolean(inputDict.sqft && inputDict.yr_built);

        // Check if we have enough for HPWH
        const hasHpwhRequirements =
            hasMinimumRequirements &&
            Boolean(
                inputDict.stories !== undefined ||
                    inputDict.has_ac !== undefined
            );

        const zipCodeValid = inputDict.zip.length === 5;

        // Return true if we have enough data for any inference type
        return (
            hasHvacRequirements ||
            hasHpwhRequirements ||
            hasMinimumRequirements ||
            zipCodeValid
        );
    }, [
        inputDict.hh_size,
        inputDict.zip,
        inputDict.sqft,
        inputDict.yr_built,
        inputDict.stories,
        inputDict.has_ac,
    ]);
}

export function useEnergyOffsetWithUpdates() {
    const [loading, setLoading] = useAtom(loadingAtom);
    const { inputDict, inputError } = useCustomerInputs();
    const [latestInput, setLatestInput] = useAtom(energyUsageLatestInputAtom);
    const [usageResult, setUsageResult] = useAtom(energyUsageResultAtom);
    const [resultInput, setResultInput] = useAtom(energyUsageResultInputAtom);
    const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const inputComplete = useIsInputComplete();

    const cancelTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = undefined;
        }
    }, []);

    useEffect(() => {
        async function update() {
            if (inputError) {
                console.log(`Input error, no need to query: ${inputError}`);
                setLoading(false);
                return;
            }

            if (!inputComplete) {
                console.log(`Input incomplete, no need to query`);
                setLoading(false);
                return;
            }

            if (isEqual(inputDict, latestInput)) {
                console.log(
                    `energyUsage latest input matches, no need to run.`
                );
                setLoading(false);
                return;
            }
            setLatestInput(inputDict);

            if (isEqual(inputDict, resultInput) && usageResult) {
                console.log(
                    `energyUsage already has correct cached result, no need to run.`
                );
                setLoading(false);
                return;
            }

            // make sure zip code is valid
            if (inputDict.zip.length !== 5) {
                console.log(`Input zip code is not valid, no need to run.`);
                setLoading(false);
                return;
            }

            setLoading(true);

            try {
                console.log(`Calling energyUsage:`, JSON.stringify(inputDict));
                const startMs = Date.now();
                const response = await callUsageCalculator(inputDict);
                const elapsedMs = Date.now() - startMs;
                console.log(
                    `energyUsage took ${elapsedMs}ms, response:`,
                    JSON.stringify(response)
                );

                if (response && response.added_usage !== undefined) {
                    console.log(`energyUsage succeeded`);
                    setUsageResult(response);
                    setErrorMessage('');
                    setResultInput(inputDict);
                } else {
                    const msg = `energyUsage failed: unexpected response format`;
                    console.warn(msg);

                    setUsageResult(undefined);
                    setErrorMessage('Failed to calculate energy usage');
                }
            } catch (e) {
                console.error(`energyUsage error:`, e);
                setUsageResult(undefined);
                try {
                    const parsedErrorOuterLayer = JSON.parse(
                        (e as any)?.message || '{}'
                    );
                    const parsedErrorInnerLayer = JSON.parse(
                        parsedErrorOuterLayer?.error || '{}'
                    );
                    console.log({ parsedErrorInnerLayer });
                    // message from error
                    console.log(Object.keys(parsedErrorInnerLayer));
                    const errorMessage = parsedErrorInnerLayer?.message;
                    setErrorMessage(errorMessage);
                } catch (e) {
                    setErrorMessage(
                        `Something went wrong please check your inputs and try again.`
                    );
                }
            } finally {
                setLoading(false);
            }
        }

        cancelTimer();
        timer.current = setTimeout(update, DEBOUNCE_DELAY_MS);
        console.log(`Enqueued next update`);
        setLoading(true);
    }, [
        cancelTimer,
        inputComplete,
        inputDict,
        inputError,
        latestInput,
        resultInput,
        setErrorMessage,
        setLatestInput,
        setLoading,
        setResultInput,
        setUsageResult,
        usageResult,
    ]);

    return {
        inputComplete,
        loading,
        usageResult,
        errorMessage: inputError || errorMessage,
    };
}

export function useEnergyOffset() {
    const [loading, setLoading] = useAtom(loadingAtom);
    const inputComplete = useIsInputComplete();
    const [usageResult, setUsageResult] = useAtom(energyUsageResultAtom);
    const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);

    return { inputComplete, loading, usageResult, errorMessage };
}
